"use client";

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@stockline/ui/components/tooltip";
import { PreOrderSourceType, type PreOrder } from "@/app/api/types/preOrder";
import { useLinkedSources } from "../hooks/useLinkedSources";
import {
  LinkedSourcesPopover,
  type LinkedSourceEntry,
} from "./LinkedSourcesPopover";
import { SourceIconCluster } from "./SourceIconCluster";
import {
  useChatPanelContextOptional,
  useRecapDataContextOptional,
} from "../contexts/RecapContext";
import { useIsSelectedPreOrder } from "../stores/chatPanelSelectionStore";
import { derivePreOrderSource, getSourceConfig } from "../utils/sourceUtils";

type SourceIconType = React.ComponentType<{ className?: string }>;

const SOURCE_COLORS: Record<number, string> = {
  [PreOrderSourceType.EMAIL]: "text-blue-600 hover:bg-blue-50",
  [PreOrderSourceType.WHATSAPP]: "text-green-600 hover:bg-green-50",
  [PreOrderSourceType.SMS]: "text-purple-600 hover:bg-purple-50",
  [PreOrderSourceType.MANUAL]: "text-gray-400 hover:bg-gray-50",
  [PreOrderSourceType.INTERNAL_OFFER]: "text-amber-600 hover:bg-amber-50",
  [PreOrderSourceType.ERP]: "text-slate-600 hover:bg-slate-50",
  [PreOrderSourceType.AI_CHAT]: "text-violet-600 hover:bg-violet-50",
};

interface PreOrderSourceButtonProps {
  preOrder: PreOrder;
  /**
   * Visual size. `"sm"` (default) matches the in-table PreOrderHeader pill
   * row. `"md"` is the focus-view variant — h-9, larger icons — so the
   * affordance reads as a peer of the other actions-bar buttons.
   */
  size?: "sm" | "md";
  /**
   * Whether the multi-source cluster shows its linked-item count badge.
   * `true` (default) for the header / focus view. The recap list view
   * passes `false` — rows are dense and the split-icon cluster already
   * signals multi-source linkage without the extra numeric badge.
   */
  showCount?: boolean;
}

/**
 * Origin-channel + linked-sources affordance for a pre-order. Renders the
 * split-icon cluster + popover when the PO carries multiple linked sources
 * (origin email/WhatsApp/SMS plus any cross-channel links), otherwise a
 * single icon-only button. Click opens the recap chat panel pinned to the
 * chosen entry — the same panel that hosts the AI assistant conversation
 * for that PO, so this is the single entrypoint into chat-side context.
 *
 * State (popover open) is local; everything else (linked-source entries,
 * panel pivot, "currently visible chat") flows through the RecapProvider /
 * chat panel stores already mounted in both surfaces (`PreOrderHeader`
 * inside the table card, `PreOrderFocusView` at the top of `/recap/:id`).
 */
export function PreOrderSourceButton({
  preOrder,
  size = "sm",
  showCount = true,
}: Readonly<PreOrderSourceButtonProps>) {
  const heightClass = size === "md" ? "h-8 w-8" : "h-7 w-7";
  const iconSizeClass = size === "md" ? "h-4 w-4" : "h-3.5 w-3.5";
  const { t } = useTranslation();
  const chatPanel = useChatPanelContextOptional();
  const data = useRecapDataContextOptional();
  const [open, setOpen] = useState(false);

  const sourceConfig = getSourceConfig(preOrder.source_type);
  const linkedSources = useLinkedSources(preOrder, {
    currentUserId: data?.user.id,
    // Gate WhatsApp/SMS entries to chats the viewer can actually open (their
    // visibility-filtered chat lists), so the icon never offers an empty
    // preview for a co-participant's chat the viewer can't see.
    visibleWhatsappChatIds: data?.visibleWhatsappChatIds,
    visibleSmsChatIds: data?.visibleSmsChatIds,
    // Pass the viewer's chat rows so sibling seats of a shared-group
    // conversation collapse to the viewer's own seat — the popover never
    // shows two rows for the same external conversation, and the default
    // click opens the seat whose WhatsApp/SMS instance the viewer owns.
    viewerWhatsappChats: data?.viewerWhatsappChats,
    viewerSmsChats: data?.viewerSmsChats,
  });

  // WhatsApp / SMS conversations are user-scoped, but the dedup fan-out gives
  // every group participant their own junction-linked chat row. "Can preview"
  // therefore means the viewer has at least one resolvable message-channel
  // source — NOT that they own the PO. Owner-only gating
  // (`preOrder.user_id === data.user.id`) greyed the icon for group members
  // who DO have their own chat copy. `useLinkedSources` already resolves the
  // viewer's linked chats from the junction-derived `linked_*_chat_ids`, so
  // the presence of a whatsapp/sms entry is the correct signal. This drives
  // the recap list rows, the focus view, and the in-table header alike.
  const isMessageSource =
    preOrder.source_type === PreOrderSourceType.WHATSAPP ||
    preOrder.source_type === PreOrderSourceType.SMS;
  const canPreviewMessages =
    !isMessageSource ||
    linkedSources.entries.some(
      (e) => e.channel === "whatsapp" || e.channel === "sms",
    );

  // Two distinct concepts:
  // - `hasMultipleOpenableSources`: 2+ entries the user can click through to,
  //   so the icon must disambiguate via a popover (e.g. 2 emails, or 1 email
  //   + 1 whatsapp).
  // - `hasMultipleChannelIcons`: 2+ icons in the cluster but only ≤1 openable
  //   conversation (e.g. manual + 1 linked whatsapp — the manual icon has no
  //   conversation to open). The cluster signals provenance, click opens the
  //   single openable entry directly. No popover would be empty / pointless.
  const hasMultipleOpenableSources = linkedSources.entries.length >= 2;
  const hasMultipleChannelIcons = linkedSources.channels.length >= 2;
  const singleChannel = hasMultipleChannelIcons
    ? undefined
    : linkedSources.channels[0];
  const SourceIcon: SourceIconType = singleChannel?.Icon ?? sourceConfig.icon;
  const sourceColor = singleChannel
    ? `${singleChannel.colorClass} ${linkedSources.triggerHoverClass}`
    : (SOURCE_COLORS[preOrder.source_type] ??
      SOURCE_COLORS[PreOrderSourceType.MANUAL]);

  // What chatId is *actually* on screen in the panel for this PO right now —
  // null when the panel is closed or showing a different PO. Drives the
  // "skip pivot" decision when the user re-clicks the trigger.
  const isSelectedInPanel = useIsSelectedPreOrder(preOrder.id);
  const activeChatIdInPanel = useMemo(() => {
    if (!isSelectedInPanel || !chatPanel?.isPanelOpen) return null;
    return chatPanel.currentSource?.chatId ?? chatPanel.selectedEmailId ?? null;
  }, [
    isSelectedInPanel,
    chatPanel?.isPanelOpen,
    chatPanel?.currentSource?.chatId,
    chatPanel?.selectedEmailId,
  ]);
  const currentChatId =
    activeChatIdInPanel ?? linkedSources.defaultEntry?.chatId;

  const openEntry = useCallback(
    (entry: LinkedSourceEntry | null) => {
      if (!chatPanel) return;
      if (!entry) {
        // No junction data — fall back to legacy resolution (e.g. manual
        // pre-orders with only an inferred conversation pin). Pass the
        // viewer-seat catalogues so the rewrite still kicks in on the rare
        // path where junction-derived entries are empty but the legacy FK
        // points at a teammate's seat.
        chatPanel.openPanel(
          preOrder.id,
          derivePreOrderSource(preOrder, {
            currentUserId: data?.user.id,
            viewerWhatsappChats: data?.viewerWhatsappChats,
            viewerSmsChats: data?.viewerSmsChats,
          }),
        );
        return;
      }
      chatPanel.openPanel(preOrder.id, {
        channel: entry.channel,
        chatId: entry.chatId,
      });
      if (entry.channel === "email") {
        // Preserve email-only override so derivedConversation can fall back
        // to selectedEmailId if the source pin clears (e.g. PO switch).
        chatPanel.setSelectedEmailId(entry.chatId);
      }
    },
    // `data` is in the deps because the fallback branch reads
    // `data?.user.id`, `data?.viewerWhatsappChats`, `data?.viewerSmsChats`
    // to thread the viewer-seat rewrite into `derivePreOrderSource`.
    // Omitting it would freeze stale viewer context on the manual-PO
    // fallback path while the recap context updates.
    [chatPanel, preOrder, data],
  );

  if (canPreviewMessages && hasMultipleOpenableSources) {
    return (
      <LinkedSourcesPopover
        open={open}
        onOpenChange={setOpen}
        entries={linkedSources.entries}
        channels={linkedSources.channels}
        triggerHoverClass={linkedSources.triggerHoverClass}
        triggerLabel={t("ordersRecap.linkedSourcesTooltip", {
          count: linkedSources.total,
        })}
        totalBadge={showCount ? linkedSources.total : undefined}
        currentChatId={currentChatId}
        size={size}
        // `onTriggerClick` is intentionally omitted — Radix toggles the
        // popover on its own and we want NO side effects on click. The
        // chat panel only opens once the user picks a specific source
        // from the dropdown (`onSelect`).
        onSelect={(entry) => openEntry(entry)}
      />
    );
  }

  if (canPreviewMessages && hasMultipleChannelIcons) {
    // ≤1 openable entry but the cluster carries 2+ icons (e.g. manual + 1
    // whatsapp). Render the cluster for provenance, but skip the popover —
    // click opens the single conversation directly. The label uses the
    // multi-source key (not `sourceConfig.label`) so the tooltip / a11y
    // name reflects what the click actually does — opening a linked
    // conversation, not the PO's origin channel (e.g. "Manuel").
    const clusterLabel = t("ordersRecap.linkedSourcesTooltip", {
      count: linkedSources.total,
    });
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <SourceIconCluster
            testId="preorder-source-button"
            channels={linkedSources.channels}
            hoverClass={linkedSources.triggerHoverClass}
            badge={showCount ? linkedSources.total : undefined}
            ariaLabel={clusterLabel}
            size={size}
            onClick={(e) => {
              e.stopPropagation();
              openEntry(linkedSources.defaultEntry);
            }}
          />
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          {clusterLabel}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {canPreviewMessages ? (
          <button
            type="button"
            data-testid="preorder-source-button"
            data-attr="preorder-source-button"
            className={`${heightClass} flex-shrink-0 flex items-center justify-center rounded border border-gray-200 transition-colors ${sourceColor}`}
            onClick={(e) => {
              // This button may sit inside a click-to-navigate recap row;
              // opening the chat panel must not also trigger row → /recap
              // navigation. Stop here, on the interactive element itself.
              e.stopPropagation();
              openEntry(linkedSources.defaultEntry);
            }}
            aria-label={sourceConfig.label}
          >
            <SourceIcon className={iconSizeClass} />
          </button>
        ) : (
          <span
            data-testid="preorder-source-indicator"
            className={`${heightClass} flex-shrink-0 flex items-center justify-center rounded border border-gray-200 text-gray-400`}
          >
            <SourceIcon className={iconSizeClass} />
          </span>
        )}
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs">
        {sourceConfig.label}
      </TooltipContent>
    </Tooltip>
  );
}
